import React, { useMemo } from "react";
import { Box, Button, Divider, TextField, Typography } from "@mui/material";
import "./PriceDetails.scss";
import { useNavigate } from "react-router-dom";
import {
  createOrderFromCart,
  createOrdersFromCartPlanner,
} from "../../../api/order-summary.api";
import { East } from "@mui/icons-material";
import { getCartCount } from "../../../api/cart.api";
import { useDispatch } from "react-redux";

interface PriceDetailsProps {
  cartSummary: any;
  partnerId: number;
  isFromDetailsPg: boolean;
  selectedPartners: number[];
  setToast: (toast: any) => void;
}

const PriceDetails: React.FC<PriceDetailsProps> = ({
  cartSummary,
  partnerId,
  isFromDetailsPg,
  selectedPartners,
  setToast,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const fetchCartCount = async () => {
    try {
      const res = await getCartCount();
      const cartCount = res.data?.data?.data?.totalCount ?? 0;
      dispatch({ type: "CART:SET_COUNT", payload: { count: cartCount } });
    } catch (error) {
      console.error("Failed to fetch cart count:", error);
    }
  };

  const effectiveSelectedPartnersList = useMemo(() => {
    const partners = Array.isArray(cartSummary?.partners)
      ? cartSummary.partners
      : [];
    const selectedSet = new Set(selectedPartners ?? []);
    const totalCount = partners.length;

    if (selectedSet.size > 0) {
      return partners.filter((p: any) => selectedSet.has(p.partnerId));
    }

    if (totalCount === 1) return partners;

    return [];
  }, [cartSummary?.partners, selectedPartners]);

  const showSummary = effectiveSelectedPartnersList.length > 0;
  const hasExplicitSelection = (selectedPartners?.length ?? 0) > 0;

  const itemCount = useMemo(() => {
    if (!showSummary) return 0;
    if (hasExplicitSelection) {
      return effectiveSelectedPartnersList.reduce((acc: number, p: any) => {
        if (Array.isArray(p?.services) && p.services.length > 0) {
          return acc + p.services.length;
        }
        if (p?.isPlannerPackage) return acc + 1;
        if (Array.isArray(p?.cartItemIds)) return acc + p.cartItemIds.length;
        return acc + (p?.partnerTotalItems ?? 1);
      }, 0);
    }
    return Number(cartSummary?.summary?.totalItems) || Number(cartSummary?.summary?.totalServices) || (cartSummary?.partners?.length ?? 0);
  }, [effectiveSelectedPartnersList, hasExplicitSelection, cartSummary, showSummary]);

  const subtotal = useMemo(() => {
    if (!showSummary) return 0;
    if (hasExplicitSelection) {
      return effectiveSelectedPartnersList.reduce(
        (acc: number, p: any) => acc + (Number(p?.partnerTotalPrice) || 0),
        0,
      );
    }
    return Number(cartSummary?.summary?.subTotal) || 0;
  }, [effectiveSelectedPartnersList, hasExplicitSelection, cartSummary, showSummary]);

  const discount = showSummary
    ? (cartSummary?.summary?.discountAmount || 0)
    : 0;

  const taxFee = showSummary
    ? (cartSummary?.summary?.taxFee || 0)
    : 0;

  const serviceFee = showSummary
    ? (cartSummary?.summary?.serviceFee || 0)
    : 0;

  const preNeedTotal = useMemo(() => {
    if (!showSummary) return 0;
    if (hasExplicitSelection) {
      return effectiveSelectedPartnersList.reduce((acc: number, p: any) => acc + (Number(p?.partnerPreNeedTypesTotalPrice) || 0), 0);
    }
    return Number(cartSummary?.summary?.PreneedTotal) || Number(cartSummary?.summary?.preNeedTotal) || 0;
  }, [effectiveSelectedPartnersList, cartSummary?.summary, hasExplicitSelection, showSummary]);

  const immediateNeedTotalValue = useMemo(() => {
    if (!showSummary) return 0;
    if (hasExplicitSelection) {
      return effectiveSelectedPartnersList.reduce((acc: number, p: any) => acc + (Number(p?.partnerAtNeedTypesTotalPrice) || 0), 0);
    }
    return Number(cartSummary?.summary?.ImmediateNeedTotal) || Number(cartSummary?.summary?.immediateNeedTotal) || 0;
  }, [effectiveSelectedPartnersList, cartSummary?.summary, hasExplicitSelection, showSummary]);


  const total = showSummary
    ? (hasExplicitSelection ? (subtotal + taxFee + serviceFee - discount) : (Number(cartSummary?.summary?.grandTotal) || (subtotal + taxFee + serviceFee - discount)))
    : 0;
  const immediateNeedTotal = immediateNeedTotalValue;

  const isPlanner = useMemo(() => {
    if (effectiveSelectedPartnersList.length > 0) {
      return effectiveSelectedPartnersList.every((p: any) => p.isPlannerPackage);
    }
    return (
      (cartSummary?.partners?.length ?? 0) > 0 &&
      cartSummary.partners.every((p: any) => p.isPlannerPackage)
    );
  }, [effectiveSelectedPartnersList, cartSummary?.partners]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const renderNeedType = (needType: string) => {

    if (!needType) return null;
    const isPreNeed = needType === "pre_need";
    const label = isPreNeed ? "Preneed" : "Immediate Need";
    return (
      <>
        <Box
          component="span"
          sx={{
            color: "#084145",
            mx: 1,
            fontWeight: 900,
            fontSize: "18px",
            verticalAlign: "middle",
          }}
        >
          &bull;
        </Box>
        <Box
          component="span"
          sx={{
            color: "#000",
            fontSize: "14px",
            fontWeight: 400,
            verticalAlign: "middle",
          }}
        >
          {label}
        </Box>
      </>
    );
  };


  const cartId = cartSummary?.cartId;

  const effectiveSelectedId = useMemo(() => {
    if ((selectedPartners?.length ?? 0) === 1) return selectedPartners[0];

    if (
      (selectedPartners?.length ?? 0) === 0 &&
      effectiveSelectedPartnersList.length === 1
    ) {
      return effectiveSelectedPartnersList[0]?.partnerId ?? null;
    }

    return null;
  }, [selectedPartners, effectiveSelectedPartnersList]);

  const handlePlaceOrder = async () => {
    try {
      if (!cartSummary || !Array.isArray(cartSummary.partners)) {
        console.warn("No order summary or partners found.");
        return;
      }

      if ((selectedPartners?.length ?? 0) > 1) {
        setToast({
          open: true,
          message:
            "You can place an order with only one provider/planner at a time. Please select a single provider to proceed.",
          severity: "warning",
        });
        return;
      }

      if (!effectiveSelectedId) {
        setToast({
          open: true,
          message:
            "Please select one provider/planner to continue with your order.",
          severity: "warning",
        });
        return;
      }

      const selectedPartner = cartSummary.partners.find(
        (p: any) => p.partnerId === effectiveSelectedId,
      );

      const isPlanner = !!selectedPartner?.isPlannerPackage;

      const plannerCartItemId =
        selectedPartner?.cartItemId ??
        selectedPartner?.plannerCartItemId ??
        selectedPartner?.cartItemIds?.[0] ??
        selectedPartner?.partnerId;

      const response = isPlanner
        ? await createOrdersFromCartPlanner({ cartItemId: plannerCartItemId })
        : await createOrderFromCart({
          cartId,
          cartItemIds: selectedPartner?.cartItemIds || [],
        });

      const responseData = response?.data?.data || response?.data;
      if (responseData && responseData.success === false) {
        setToast({
          open: true,
          message: responseData.message || "Failed to create order",
          severity: "error",
        });
        return;
      }

      await fetchCartCount();

      const orderIdFromApi =
        response?.data?.data?.data?.orderId ??
        response?.data?.data?.data?.order?.orderId ??
        response?.data?.data?.orderId ??
        response?.data?.orderId;

      navigate(`/order-summary`, {
        state: {
          orderId: orderIdFromApi,
          partnerId: effectiveSelectedId,
          isFromCartPage: true,
          isFromDetailsPg: isFromDetailsPg,
          isFromPlans: isPlanner,
          plannerId: selectedPartner?.plannerId,
        },
      });
    } catch (error: any) {
      console.error("ERROR IN HANDLE PLACE ORDER::", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.data?.message ||
        "Something went wrong while placing your order. Please try again.";
      setToast({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    }
  };

  return (
    <Box className="cart-summary">
      <Box className="cart-summary__box cart-summary__box--top">
        <Box className="cart-summary__row">
          <Typography className="cart-summary__label">
            {itemCount} {itemCount === 1 ? "item" : "items"}
          </Typography>
          <Typography className="cart-summary__value">
            $ {subtotal ?? 0}.00
          </Typography>
        </Box>
      </Box>

      {/* <Box className="cart-summary__box cart-summary__box--middle">
        <TextField
          placeholder="Have a coupon code?"
          className="cart-summary__input-field"
          disabled
        />
        <Button className="cart-summary__apply-btn" variant="outlined">
          Apply
        </Button>
      </Box> */}

      <Box className="cart-summary__box cart-summary__box--details">
        {effectiveSelectedPartnersList.map((partner: any, index: number) => {
          if (Array.isArray(partner?.services) && partner.services.length > 0) {
            return partner.services.map(
              (service: any, serviceIndex: number) => (
                <Box
                  className="cart-summary__row"
                  key={`${index}-service-${serviceIndex}`}
                >
                  <Typography className="cart-summary__label">
                    {service?.serviceName || "-"}
                    {renderNeedType(service?.needType)}
                  </Typography>
                  <Typography className="cart-summary__value">
                    $ {formatCurrency(service?.serviceTotalPrice ?? service?.price ?? 0)}
                  </Typography>
                </Box>
              ),
            );
          }

          if (partner?.isPlannerPackage) {
            return (
              <Box key={index}>
                <Box className="cart-summary__row" sx={{ marginBottom: "0px !important" }}>
                  {partner?.packageName && (
                    <Typography sx={{ fontWeight: "600 !important", fontSize: "14px", color: "#000" }}>
                      {partner.packageName}
                    </Typography>
                  )}
                  <Typography className="cart-summary__value">
                    $ {formatCurrency(partner?.packagePrice ?? partner?.partnerTotalPrice ?? 0)}
                  </Typography>
                </Box>

              </Box>
            );
          }

          return (
            <Box className="cart-summary__row" key={index}>
              <Typography className="cart-summary__label">
                {partner?.partnerName || "-"}
                {renderNeedType(partner?.partnerPreNeedTypesTotalPrice > 0 ? "pre_need" : "at_need")}
              </Typography>
              <Typography className="cart-summary__value">
                $ {formatCurrency(partner?.partnerTotalPrice ?? 0)}
              </Typography>
            </Box>
          );
        })}

        {discount > 0 && (
          <Box className="cart-summary__row">
            <Typography className="cart-summary__discount-label">
              Discount
            </Typography>
            <Typography className="cart-summary__discount-value">
              $ {formatCurrency(discount)}
            </Typography>
          </Box>
        )}

        <Box className="cart-summary__row">
          <Typography className="cart-summary__label">Taxes</Typography>
          <Typography className="cart-summary__value">
            $ {formatCurrency(taxFee)}
          </Typography>
        </Box>

        <Divider className="cart-summary__divider" />

        <Box className="cart-summary__row cart-summary__row--total">
          <Typography className="cart-summary__label">Total*</Typography>
          <Typography className="cart-summary__value">
            $ {formatCurrency(total)}
          </Typography>
        </Box>

        {showSummary && !isPlanner && (
          <>
            <Divider className="cart-summary__divider" />

            <Box className="cart-summary__row">
              <Typography className="cart-summary__label">Preneed Total</Typography>
              <Typography className="cart-summary__value">
                $ {formatCurrency(preNeedTotal)}
              </Typography>
            </Box>

            <Box className="cart-summary__row">
              <Typography className="cart-summary__label">
                Immediate Need Total
              </Typography>
              <Typography className="cart-summary__value">
                $ {formatCurrency(immediateNeedTotal)}
              </Typography>
            </Box>
          </>
        )}

        <Typography className="cart-summary__billing-info">
          *Billing applies only to Immediate Need Services and Preneed Planner on MemorialGo. Preneed Service payments will be finalized with the Provider.
        </Typography>

      </Box>

      <Button
        fullWidth
        className="cart-summary__btn"
        onClick={handlePlaceOrder}
        endIcon={<East />}
      >
        Place Order
      </Button>
    </Box>
  );
};

export default PriceDetails;
